import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Clock, X } from 'lucide-react';
import { fastingApi } from '../api/fasting';

const PRESETS = ['16:8', '18:6', '20:4', 'OMAD', 'custom'];

export default function FastingProtocolCard({ client, onUpdated }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const current = client?.fasting_protocol || null;

  const [preset, setPreset] = useState(current?.preset || '16:8');
  const [customHours, setCustomHours] = useState(current?.custom_hours || 14);

  const saveMutation = useMutation({
    mutationFn: () => fastingApi.setClientProtocol(
      client.id, preset, preset === 'custom' ? Number(customHours) : null,
    ),
    onSuccess: () => {
      toast.success(t('fasting.protocolSaved'));
      qc.invalidateQueries({ queryKey: ['coach-client', client.id] });
      onUpdated?.();
    },
    onError: () => toast.error(t('fasting.saveError')),
  });

  const clearMutation = useMutation({
    mutationFn: () => fastingApi.clearClientProtocol(client.id),
    onSuccess: () => {
      toast.success(t('fasting.protocolCleared'));
      qc.invalidateQueries({ queryKey: ['coach-client', client.id] });
      onUpdated?.();
    },
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <div className="font-semibold">{t('fasting.protocolTitle')}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p}
              onClick={() => setPreset(p)}
              className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm border transition active:scale-[0.97] ${
                preset === p
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border'
              }`}>
              {p === 'custom' ? t('fasting.custom') : p}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <Input type="number" inputMode="numeric" min={1} max={36}
                 value={customHours}
                 onChange={(e) => setCustomHours(e.target.value)}
                 className="h-11" />
        )}
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {t('common.save')}
          </Button>
          {current && (
            <Button variant="outline" onClick={() => clearMutation.mutate()} disabled={clearMutation.isPending}>
              <X className="h-4 w-4 mr-1" />
              {t('common.remove')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
